/**
 * Tabbed Products section: tab switching, carousel updates, and accessibility.
 * Scoped per section so multiple instances on the same page work independently.
 */

(function () {
  'use strict';

  const ARROW_KEYS = {
    ArrowRight: 1,
    ArrowDown: 1,
    ArrowLeft: -1,
    ArrowUp: -1
  };

  function updateCarouselSlideAccessibility(carouselWrapper, swiper) {
    if (!swiper?.slides) return;

    const slideCount =
      parseInt(carouselWrapper.dataset.slideCount, 10) || swiper.slides.length;
    const isGridLayout = window.matchMedia('(min-width: 1024px)').matches;

    swiper.slides.forEach(function (slide) {
      if (!(slide instanceof HTMLElement)) return;

      const visible = isGridLayout || slide.classList.contains('swiper-slide-visible');

      slide.setAttribute('aria-hidden', visible ? 'false' : 'true');

      if (visible) {
        slide.removeAttribute('inert');
      } else {
        slide.setAttribute('inert', '');
      }
    });

    const liveRegion = carouselWrapper.querySelector('[data-tabbed-products-carousel-status]');
    if (!liveRegion || isGridLayout) return;

    const activeIndex =
      typeof swiper.activeIndex === 'number' ? swiper.activeIndex + 1 : 1;
    const template = carouselWrapper.dataset.slideStatusTemplate;

    liveRegion.textContent = template
      ? template
          .replace('__INDEX__', String(activeIndex))
          .replace('__COUNT__', String(slideCount))
      : 'Slide ' + activeIndex + ' of ' + slideCount;
  }

  function bindCarouselAccessibility(section, carouselWrapper, swiper) {
    if (!swiper || carouselWrapper.dataset.a11yInit === 'true') return;

    carouselWrapper.dataset.a11yInit = 'true';

    const syncSlides = function () {
      updateCarouselSlideAccessibility(carouselWrapper, swiper);
    };

    syncSlides();
    swiper.on('slideChange', syncSlides);
    swiper.on('resize', syncSlides);
    swiper.on('breakpoint', syncSlides);

    window.matchMedia('(min-width: 1024px)').addEventListener('change', syncSlides);
  }

  function initCarouselAccessibility(section) {
    section.querySelectorAll('s-carousel').forEach(function (carouselWrapper) {
      function handleReady(event) {
        if (event.detail?.swiper) {
          bindCarouselAccessibility(section, carouselWrapper, event.detail.swiper);
        }
      }

      carouselWrapper.addEventListener('carousel:ready', handleReady);

      const swiperEl = carouselWrapper.querySelector('[js-carousel]');
      if (swiperEl?.swiper) {
        bindCarouselAccessibility(section, carouselWrapper, swiperEl.swiper);
      }
    });
  }

  function initTabbedProducts(section) {
    if (!section || section.dataset.tabbedProductsInited) return;
    section.dataset.tabbedProductsInited = 'true';

    const tabs = section.querySelectorAll('[data-tabbed-products-tab]');
    const panels = section.querySelectorAll('.tabbed-products__panel');
    if (!tabs.length || !panels.length) return;

    function showPanel(panelId) {
      tabs.forEach(function (tab) {
        const isActive = tab.getAttribute('aria-controls') === panelId;
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.setAttribute('tabindex', isActive ? '0' : '-1');
      });

      panels.forEach(function (panel) {
        const isActive = panel.id === panelId;
        panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        panel.setAttribute('tabindex', isActive ? '0' : '-1');

        if (isActive) {
          panel.removeAttribute('inert');
        } else {
          panel.setAttribute('inert', '');
        }
      });

      const activePanel = document.getElementById(panelId);
      if (!activePanel) return;

      const carouselEl = activePanel.querySelector('[js-carousel]');
      if (carouselEl?.swiper) {
        carouselEl.swiper.updateSize();
        carouselEl.swiper.update();

        const carouselWrapper = activePanel.querySelector('s-carousel');
        if (carouselWrapper) {
          updateCarouselSlideAccessibility(carouselWrapper, carouselEl.swiper);
        }
      }
    }

    function handleTabClick(e) {
      const panelId = e.currentTarget.getAttribute('aria-controls');
      if (panelId) showPanel(panelId);
    }

    function handleKeydown(e) {
      const tabArray = Array.from(tabs);
      const currentIdx = tabArray.findIndex(function (tab) {
        return tab.getAttribute('aria-selected') === 'true';
      });
      if (currentIdx === -1) return;

      if (e.key === 'Home') {
        e.preventDefault();
        tabArray[0].click();
        tabArray[0].focus();
        return;
      }

      if (e.key === 'End') {
        e.preventDefault();
        tabArray[tabArray.length - 1].click();
        tabArray[tabArray.length - 1].focus();
        return;
      }

      const delta = ARROW_KEYS[e.key];
      if (delta === undefined) return;

      e.preventDefault();

      const nextIdx = currentIdx + delta;
      if (nextIdx >= 0 && nextIdx < tabArray.length) {
        tabArray[nextIdx].click();
        tabArray[nextIdx].focus();
      }
    }

    tabs.forEach(function (tab) {
      tab.addEventListener('click', handleTabClick);
    });

    const tablist = section.querySelector('[role="tablist"]');
    if (tablist) {
      tablist.addEventListener('keydown', handleKeydown);
    }

    initCarouselAccessibility(section);
  }

  function initAll(container) {
    const root = container || document;
    root.querySelectorAll('[data-tabbed-products]').forEach(initTabbedProducts);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    const section = event.target.querySelector('[data-tabbed-products]');
    if (!section) return;

    delete section.dataset.tabbedProductsInited;

    section.querySelectorAll('s-carousel').forEach(function (carouselWrapper) {
      delete carouselWrapper.dataset.a11yInit;
    });

    section.querySelectorAll('[js-carousel].swiper-initialized').forEach(function (carouselEl) {
      if (carouselEl.swiper && carouselEl.swiper.destroy) {
        carouselEl.swiper.destroy(true, true);
      }
    });
  });
})();
